import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import type { GrievanceAction } from "@/api/admin";
import { getTestTicket } from "@/api/test-tickets";
import { Spinner } from "@/components/Spinner";
import { formatDateTime } from "@/lib/formatDate";
import { AdminHeader } from "@/pages/admin/components/AdminHeader";
import { AIResponseEditor } from "@/pages/admin/components/AIResponseEditor";
import { AttachmentPanel } from "@/pages/admin/components/AttachmentPanel";
import { HistoryDisclosure } from "@/pages/admin/components/HistoryDisclosure";
import { useCurrentTabId } from "@/pages/admin/TabsContext";

export function TestTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const testTicketId = Number(id);
  const tabId = useCurrentTabId();

  const q = useQuery({
    queryKey: ["test-ticket", tabId, testTicketId],
    queryFn: () => getTestTicket(tabId, testTicketId),
    enabled: Number.isFinite(testTicketId) && !!tabId,
  });

  return (
    <div className="min-h-screen flex flex-col">
      <AdminHeader
        title="Test ticket workspace"
        backTo={`/admin/tabs/${tabId}/test-tickets`}
      />
      <main className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-14 py-6 flex-1 flex flex-col gap-5">
        {q.isLoading ? (
          <div className="card p-12 flex justify-center">
            <Spinner label="Loading test ticket…" />
          </div>
        ) : q.isError ? (
          <div className="alert-danger">{(q.error as Error)?.message}</div>
        ) : q.data ? (
          <>
            <div className="alert-info text-xs">
              This is a <strong>test ticket</strong>. All writes land in Postgres. Sending a reply
              does <strong>not</strong> embed the answer into this tab's Qdrant collection – the
              real KB stays clean.
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <h1 className="text-xl sm:text-2xl font-bold">
                Test Ticket <span className="font-mono">#{q.data.id}</span>
              </h1>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoBox label="Application ID" value={q.data.application_id} mono />
              <InfoBox label="Submitted On" value={formatDateTime(q.data.sent_datetime)} mono />
            </div>

            <section className="card p-4 space-y-2">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Student's Ticket
              </h3>
              <p className="whitespace-pre-wrap leading-relaxed text-sm">{q.data.grievance}</p>
            </section>

            <div className="card p-4">
              <AIResponseEditor
                mode="test"
                ticketId={q.data.id}
                applicationId={q.data.application_id}
                botResponse={q.data.bot_response}
                finalReply={q.data.replied_grievance}
                repliedBy={q.data.replied_by}
              />
            </div>

            {q.data.has_attachment && (
              <AttachmentPanel tabId={tabId} grievanceId={q.data.id} mode="test" />
            )}

            <ResponseHistory actions={q.data.actions} />

            <HistoryDisclosure
              tabId={tabId}
              grievanceId={q.data.id}
              applicationId={q.data.application_id}
              mode="test"
            />
          </>
        ) : null}
      </main>
    </div>
  );
}

function ResponseHistory({ actions }: { actions: GrievanceAction[] }) {
  if (!actions || actions.length === 0) return null;
  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Response history (this test ticket)
      </h3>
      <ul className="space-y-3">
        {actions.map((a) => (
          <li
            key={a.id}
            className="rounded-md border border-canvas-border dark:border-surface-border p-3 text-sm"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <span className="font-medium">
                  {a.admin_full_name || a.admin_username || <em className="text-slate-400">unknown admin</em>}
                </span>
                <span className="ml-2 text-xs text-slate-500">{formatDateTime(a.acted_at)}</span>
              </div>
              <ActionBadge type={a.action_type} />
            </div>
            {a.action_type === "edited_submitted" && a.ai_text && a.edited_text && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-200">
                  Show AI original vs edited
                </summary>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">AI original</p>
                    <p className="whitespace-pre-wrap text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                      {a.ai_text}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Submitted</p>
                    <p className="whitespace-pre-wrap text-xs leading-relaxed">{a.edited_text}</p>
                  </div>
                </div>
              </details>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ActionBadge({ type }: { type: GrievanceAction["action_type"] }) {
  const cfg: Record<GrievanceAction["action_type"], { label: string; className: string }> = {
    generated: {
      label: "Generated AI draft",
      className: "bg-slate-200 text-slate-700 dark:bg-surface-border dark:text-slate-200",
    },
    submitted: {
      label: "Submitted as-is",
      className:
        "bg-brand-green/20 text-status-ackFg dark:bg-brand-green/20 dark:text-brand-green",
    },
    edited_submitted: {
      label: "Edited & submitted",
      className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    },
  };
  const c = cfg[type];
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${c.className}`}>
      {c.label}
    </span>
  );
}

function InfoBox({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="card px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</p>
      <p className={`mt-1 text-sm ${mono ? "font-mono" : ""} truncate`} title={value}>
        {value}
      </p>
    </div>
  );
}
